/**
 * Receipt / payment vouchers (سند قبض / سند دفع). One component, two modes via
 * the `type` prop — a cash document that records money received into (receipt)
 * or paid from (payment) a cash/bank account and posts a balanced GL entry:
 *   receipt:  DR cash/bank   CR counter (income / receivable)
 *   payment:  DR counter (expense / payable)   CR cash/bank
 * Modelled on ExpensesPage; reuses gl.post permission.
 */
import type { Account, Party, Voucher, VoucherType } from '@erp/shared';
import { ArrowRight, Ban, Banknote, Calendar, Coins, FileText, HandCoins, Hash, type LucideIcon, Pencil, Plus, Trash2, User, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Button,
  Combobox,
  type ComboboxOption,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  NumberInput,
  StatCard,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { formatAccountBalance, loadAccountBalances, type AccountBalanceMap } from '../../lib/accountBalances.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { appUrl } from '../../lib/basePath.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { fetchAllParties } from '../../lib/parties.js';
import { createVoucher, deleteVoucher, listVouchers, updateVoucher, voidVoucher } from '../../lib/vouchers.js';
import { FxEquivalent } from '../common/FxEquivalent.js';
import { partyPath } from '../documents/partyPath.js';

/** Builds a Combobox option for an account, tagging its current balance as the dim sublabel. */
function acctOption(a: Account, sub: (id: string) => string | undefined): ComboboxOption {
  return { value: a.id, label: `${a.code} — ${a.name}`, sublabel: sub(a.id), keywords: `${a.code} ${a.name}` };
}

/**
 * The selected account's current balance, right under its picker — so you can
 * see what is actually in the account without leaving the voucher.
 */
function AccountBalance({ accountId, label }: { accountId?: string; label: (id: string) => string }): JSX.Element | null {
  if (!accountId) return null;
  return <p className="mt-1 text-xs font-semibold tabular-nums text-brand">{label(accountId)}</p>;
}

/** Cash/bank accounts (asset + subtype cash|bank); falls back to any active asset, then all. */
function cashAcctOptions(accounts: Account[], sub: (id: string) => string | undefined): ComboboxOption[] {
  const cash = accounts.filter((a) => a.isActive && a.accountType === 'asset' && (a.accountSubtype === 'cash' || a.accountSubtype === 'bank'));
  const list = cash.length ? cash : accounts.filter((a) => a.isActive && a.accountType === 'asset');
  const final = list.length ? list : accounts.filter((a) => a.isActive);
  return final.map((a) => acctOption(a, sub));
}

/** Counter accounts: any active account, with the natural type for this voucher (revenue for a receipt, expense for a payment) floated to the top. */
function counterAcctOptions(accounts: Account[], type: VoucherType, sub: (id: string) => string | undefined): ComboboxOption[] {
  const preferred = type === 'receipt' ? ['revenue', 'liability'] : ['expense', 'liability'];
  return accounts
    .filter((a) => a.isActive)
    .slice()
    .sort((a, b) => {
      const ap = preferred.includes(a.accountType) ? 0 : 1;
      const bp = preferred.includes(b.accountType) ? 0 : 1;
      return ap - bp || a.code.localeCompare(b.code);
    })
    .map((a) => acctOption(a, sub));
}

export function VouchersPage({ type }: { type: VoucherType }): JSX.Element {
  const { t, i18n } = useTranslation('vouchers');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('gl.post');
  // Gates the party-name link: /sales/parties/:id is behind `parties.read`.
  const canReadParties = usePermission('parties.read');
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  const locale = i18n.language;

  const [vouchers, setVouchers] = useState<Voucher[] | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  // accountId → current base-currency balance, shown under each account in the pickers.
  const [balances, setBalances] = useState<AccountBalanceMap>(new Map());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Voucher | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');

  const reload = async (): Promise<void> => {
    setVouchers(await listVouchers({ type, from: from || undefined, to: to || undefined, status: status || undefined }));
  };
  // Account balances (a nice-to-have for the pickers) — refreshed after any posting change.
  const loadBalances = async (): Promise<void> => {
    try {
      setBalances(await loadAccountBalances());
    } catch { /* balances stay as-is */ }
  };
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, from, to, status]);
  /**
   * EVERY party, customers and vendors alike — a voucher can be received from or
   * paid to either. Do NOT pass `partyType: 'both'`: the API reads that as "only
   * parties whose type is literally both", which is almost none, so the picker
   * came back empty and the search looked broken. Paged (the endpoint caps at
   * 500 a call) so the picker offers the whole book, not its first page.
   */
  useEffect(() => {
    void api().get<{ items: Account[] }>('/accounts').then((r) => setAccounts(r.data.items)).catch(() => undefined);
    void fetchAllParties<Party>().then(setParties).catch(() => undefined);
    void loadBalances();
  }, []);

  /**
   * The receivables / payables account. A voucher booked against it for a named
   * party IS that party paying their account down, and the server settles their
   * open documents accordingly — so the form can offer it as a switch and the
   * list can say which vouchers did it.
   */
  const [settleAccountId, setSettleAccountId] = useState<string | null>(null);
  useEffect(() => {
    void api()
      .get<{ arAccountId: string | null; apAccountId: string | null }>('/tenants/current')
      .then((r) => setSettleAccountId((type === 'receipt' ? r.data.arAccountId : r.data.apAccountId) ?? null))
      // Not configured, or not readable — the switch simply doesn't appear.
      .catch(() => setSettleAccountId(null));
  }, [type]);
  const settlesDebt = (v: Voucher): boolean =>
    !!settleAccountId && !!v.partyId && v.counterAccountId === settleAccountId;
  // Reset the inline form when switching between receipt / payment pages.
  useEffect(() => { setShowForm(false); setEditing(null); }, [type]);

  const m = (amount: string, currency: string): string => formatMoney(amount, { locale, currency });

  const totals = useMemo(() => {
    const byCcy = new Map<string, number>();
    for (const v of vouchers ?? []) {
      if (v.status !== 'posted') continue;
      byCcy.set(v.currencyCode, (byCcy.get(v.currencyCode) ?? 0) + Number(v.amount));
    }
    return [...byCcy.entries()];
  }, [vouchers]);

  /**
   * Which of the two profile routes this voucher's party belongs on. Money IN is
   * a customer, money OUT is a supplier — the same inbound/outbound call the
   * payments list makes, so `partyPath` lands them on the hat they were wearing
   * and the page's back-link points at a list they are actually on.
   */
  const partyHref = (partyId: string): string =>
    partyPath('/vouchers', partyId, type === 'receipt' ? 'inbound' : 'outbound');

  const onVoid = async (v: Voucher): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmVoid', { number: v.voucherNumber }), tone: 'danger' }))) return;
    try { await voidVoucher(v.id); await reload(); await loadBalances(); } catch { toast.error(tc('errors:server')); }
  };

  const onDelete = async (v: Voucher): Promise<void> => {
    const message = v.status === 'void'
      ? t('actions.confirmDelete', { number: v.voucherNumber })
      : t('actions.confirmDeletePosted', { number: v.voucherNumber });
    if (!(await confirmDialog({ message, tone: 'danger' }))) return;
    try { await deleteVoucher(v.id); await reload(); await loadBalances(); } catch { toast.error(tc('errors:server')); }
  };

  const columns: DataTableColumn<Voucher>[] = [
    {
      id: 'number', header: t('columns.number'), sortValue: (v) => v.voucherNumber, searchValue: (v) => v.voucherNumber,
      cell: (v) => (v.journalEntryId ? (
        <button type="button" className="font-mono text-xs text-brand hover:underline" onClick={() => window.open(appUrl(`/accounting/journals/${v.journalEntryId}/print`), '_blank')} title={t('viewVoucher')}>
          {v.voucherNumber}
        </button>
      ) : <span className="font-mono text-xs">{v.voucherNumber}</span>),
    },
    { id: 'date', header: t('columns.date'), sortValue: (v) => v.voucherDate, cell: (v) => formatDate(v.voucherDate, { locale }) },
    {
      // The name opens the party's profile — the voucher tells you money moved,
      // the profile tells you what they still owe. Only a link where it can
      // actually be followed: without `parties.read` that route answers with a
      // permission refusal, so the name stays plain text.
      id: 'party', header: t('columns.party'), sortValue: (v) => v.partyName ?? '', searchValue: (v) => v.partyName ?? '',
      cellClassName: 'font-medium',
      cell: (v) => {
        if (!v.partyName) return <span className="text-fg-muted">—</span>;
        const name = !canReadParties || !v.partyId ? <>{v.partyName}</> : (
          <Link to={partyHref(v.partyId)} className="text-fg underline-offset-4 hover:text-brand hover:underline" title={t('viewParty', { defaultValue: 'Open profile' })}>
            {v.partyName}
          </Link>
        );
        // Says which vouchers actually moved the party's debt. Without it the
        // two kinds look identical on the list and the only way to tell is to
        // open each one and read its counter account.
        if (!settlesDebt(v)) return name;
        return (
          <span className="flex flex-col items-start gap-0.5">
            {name}
            <Badge variant="info">{t('settle.badge', { defaultValue: 'Off their account' })}</Badge>
          </span>
        );
      },
    },
    {
      id: 'counter', header: type === 'receipt' ? t('columns.receivedFor') : t('columns.paidFor'), sortValue: (v) => v.counterAccountName ?? '',
      cellClassName: 'text-fg-muted', cell: (v) => v.counterAccountName ?? '—',
    },
    {
      id: 'cash', header: type === 'receipt' ? t('columns.receivedInto') : t('columns.paidFrom'), sortValue: (v) => v.cashAccountName ?? '',
      cellClassName: 'text-fg-muted', cell: (v) => v.cashAccountName ?? '—',
    },
    {
      id: 'amount', header: t('columns.amount'), align: 'end', sortValue: (v) => Number(v.amount), cellClassName: 'font-mono tabular-nums',
      cell: (v) => <span className={v.status === 'void' ? 'text-fg-muted line-through' : ''}>{m(v.amount, v.currencyCode)}</span>,
    },
    {
      id: 'status', header: t('columns.status'), sortValue: (v) => v.status,
      cell: (v) => <Badge variant={v.status === 'posted' ? 'success' : 'secondary'}>{t(`status.${v.status}`)}</Badge>,
    },
    {
      id: 'actions', header: '', align: 'end' as const, headClassName: 'w-32',
      cell: (v: Voucher) => (
        <div className="flex justify-end gap-1">
          {canWrite && v.status === 'posted' && (
            <Button variant="ghost" size="sm" onClick={() => { setEditing(v); setShowForm(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} title={tc('actions.edit')} aria-label={`${tc('actions.edit')} ${v.voucherNumber}`}>
              <Pencil className="h-4 w-4" aria-hidden />
            </Button>
          )}
          {canWrite && v.status === 'posted' && (
            <Button variant="ghost" size="sm" onClick={() => void onVoid(v)} title={t('actions.void')} aria-label={`${t('actions.void')} ${v.voucherNumber}`}>
              <Ban className="h-4 w-4" aria-hidden />
            </Button>
          )}
          {canWrite && (
            <Button variant="ghost" size="sm" onClick={() => void onDelete(v)} title={t('actions.delete')} aria-label={`${t('actions.delete')} ${v.voucherNumber}`}>
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t(`${type}.title`)}</h1>
          <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t(`${type}.description`)}</p>
        </div>
        {canWrite && !showForm && !editing && (
          <Button onClick={() => setShowForm(true)}><Plus className="me-1.5 h-4 w-4" aria-hidden />{t(`${type}.new`)}</Button>
        )}
      </header>

      {vouchers && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {(totals.length ? totals.slice(0, 2) : [[baseCurrency, 0] as [string, number]]).map(([ccy, amt]) => (
            <StatCard key={ccy} label={t('kpi.total')} value={m(Number(amt).toFixed(4), ccy)} icon={HandCoins} tone={type === 'receipt' ? 'success' : 'warning'} />
          ))}
          <StatCard label={t('kpi.count')} value={formatNumber(vouchers.length, { locale })} icon={Banknote} />
        </div>
      )}

      {(showForm || editing) && (
        <VoucherForm
          key={editing?.id ?? 'new'}
          type={type}
          voucher={editing ?? undefined}
          accounts={accounts}
          parties={parties}
          balances={balances}
          settleAccountId={settleAccountId}
          baseCurrency={baseCurrency}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSaved={async () => { setShowForm(false); setEditing(null); await reload(); await loadBalances(); }}
        />
      )}

      <Card className="flex flex-wrap items-center gap-3 p-3">
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          {t('filters.from')}
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-auto" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          {t('filters.to')}
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-auto" />
        </label>
        <select className="h-9 rounded-md border border-border bg-bg px-3 text-sm text-fg" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('filters.status')}>
          <option value="">{t('filters.allStatuses')}</option>
          <option value="posted">{t('status.posted')}</option>
          <option value="void">{t('status.void')}</option>
        </select>
      </Card>

      {!vouchers ? (
        <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
      ) : vouchers.length === 0 ? (
        <EmptyState
          icon={<HandCoins className="h-8 w-8" aria-hidden />}
          title={t(`${type}.empty`)}
          description={t(`${type}.emptyDescription`)}
          action={canWrite ? <Button size="sm" onClick={() => setShowForm(true)}>{t(`${type}.new`)}</Button> : undefined}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={vouchers} rowKey={(v) => v.id} searchPlaceholder={t('columns.party')} />
        </Card>
      )}
    </div>
  );
}

interface VoucherFormProps {
  type: VoucherType;
  voucher?: Voucher;
  accounts: Account[];
  parties: Party[];
  balances: AccountBalanceMap;
  /** Receivables (receipt) / payables (payment) account, or null if unconfigured. */
  settleAccountId: string | null;
  baseCurrency: string;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function VoucherForm({ type, voucher, accounts, parties, balances, settleAccountId, baseCurrency, onCancel, onSaved }: VoucherFormProps): JSX.Element {
  const { t, i18n } = useTranslation('vouchers');
  const { t: tc } = useTranslation();
  const isEdit = !!voucher;
  const [date, setDate] = useState(() => voucher?.voucherDate ?? new Date().toISOString().slice(0, 10));
  const [partyId, setPartyId] = useState<string | undefined>(voucher?.partyId ?? undefined);
  const [cashAccountId, setCashAccountId] = useState<string | undefined>(voucher?.cashAccountId ?? undefined);
  const [counterAccountId, setCounterAccountId] = useState<string | undefined>(voucher?.counterAccountId ?? undefined);
  const [amount, setAmount] = useState(voucher ? String(Number(voucher.amount)) : '');
  const [currency, setCurrency] = useState(voucher?.currencyCode ?? baseCurrency);
  const [reference, setReference] = useState(voucher?.reference ?? '');
  const [notes, setNotes] = useState(voucher?.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Dim secondary line under each account = its current balance in the base
   * currency. The trial balance only lists accounts that have MOVED, so an
   * untouched account has no row — show it as zero rather than blank, otherwise
   * most accounts appear to have no balance at all.
   */
  const balanceLabel = (id: string): string =>
    `${t('form.balance')}: ${formatAccountBalance(balances.get(id), i18n.language, baseCurrency)}`;
  const balanceSub = (id: string): string | undefined => balanceLabel(id);
  const cashAccts = cashAcctOptions(accounts, balanceSub);
  const counterAccts = counterAcctOptions(accounts, type, balanceSub);
  const partyCombo: ComboboxOption[] = parties.map((p) => ({ value: p.id, label: p.displayName, keywords: `${p.displayName} ${p.code ?? ''}` }));

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!date || !cashAccountId || !counterAccountId || !amount.trim()) { setError(t('errors.required')); return; }
    if (cashAccountId === counterAccountId) { setError(t('errors.sameAccount')); return; }
    if (!(Number(amount) > 0)) { setError(t('errors.invalidAmount')); return; }
    setSaving(true);
    try {
      const base = {
        voucherDate: date,
        partyId: partyId ?? null,
        cashAccountId,
        counterAccountId,
        amount: amount.trim(),
        currencyCode: currency,
        reference: reference.trim() || null,
        description: notes.trim() || null,
      };
      if (isEdit) await updateVoucher(voucher.id, base);
      else await createVoucher({ ...base, voucherType: type });
      await onSaved();
    } catch (err) {
      const cause = (err as { response?: { data?: { cause?: string } } }).response?.data?.cause;
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'currency.rate_not_found' ? tc('fx.rateMissing')
        : cause === 'same_account' ? t('errors.sameAccount') : cause === 'invalid_amount' ? t('errors.invalidAmount') : t('errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const fieldLabel = (Icon: LucideIcon, text: string): JSX.Element => (
    <span className="inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-fg-subtle" aria-hidden />{text}</span>
  );
  const acctName = (id?: string): string => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.code} — ${a.name}` : '';
  };
  const previewReady = !!cashAccountId && !!counterAccountId && Number(amount) > 0;
  // receipt: DR cash → CR counter; payment: DR counter → CR cash.
  const drName = type === 'receipt' ? acctName(cashAccountId) : acctName(counterAccountId);
  const crName = type === 'receipt' ? acctName(counterAccountId) : acctName(cashAccountId);

  return (
    <Card className="p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">{isEdit ? t('form.editTitle', { number: voucher.voucherNumber }) : t(`${type}.formTitle`)}</h2>
        {isEdit && <p className="mt-1 text-xs text-fg-muted">{t('form.editHint')}</p>}
      </div>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={submit} noValidate>
        <FormField id="v-date" label={fieldLabel(Calendar, t('form.date'))} required>
          <Input id="v-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </FormField>
        <FormField id="v-party" label={fieldLabel(User, type === 'receipt' ? t('form.receivedFrom') : t('form.paidTo'))}>
          <Combobox id="v-party" options={partyCombo} value={partyId} onChange={(v) => setPartyId(v ?? undefined)} placeholder={t('form.searchParty')} ariaLabel={t('form.party')} />
          {/* The whole point of the switch: a voucher booked to receivables for
              a named party is that party paying their account down, and the
              server settles their open documents off it. Ticking it just points
              the counter account there — the accounting rule and the button are
              the same thing, so they cannot drift apart. Offered only once
              somebody is named, because there is no account without one. */}
          {partyId && settleAccountId && (
            <label className="mt-2 flex cursor-pointer items-start gap-2 rounded-md border border-border bg-bg-subtle p-2.5 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[var(--color-brand)]"
                checked={counterAccountId === settleAccountId}
                onChange={(e) => setCounterAccountId(e.target.checked ? settleAccountId : undefined)}
              />
              <span>
                <span className="font-medium text-fg">
                  {type === 'receipt'
                    ? t('settle.receipt', { defaultValue: "Settle this customer's debt" })
                    : t('settle.payment', { defaultValue: "Settle this supplier's account" })}
                </span>
                <span className="mt-0.5 block text-xs text-fg-muted">
                  {type === 'receipt'
                    ? t('settle.receiptHint', { defaultValue: 'The amount comes off their open invoices, oldest first.' })
                    : t('settle.paymentHint', { defaultValue: 'The amount comes off their open bills, oldest first.' })}
                </span>
              </span>
            </label>
          )}
        </FormField>
        <FormField id="v-cash" label={fieldLabel(Wallet, type === 'receipt' ? t('form.receivedInto') : t('form.paidFrom'))} required>
          <Combobox id="v-cash" options={cashAccts} value={cashAccountId} onChange={(v) => setCashAccountId(v ?? undefined)} placeholder={t('form.searchCash')} ariaLabel={t('form.cashAccount')} />
          <AccountBalance accountId={cashAccountId} label={balanceLabel} />
        </FormField>
        <FormField id="v-counter" label={fieldLabel(Banknote, type === 'receipt' ? t('form.receivedFor') : t('form.paidFor'))} required>
          <Combobox id="v-counter" options={counterAccts} value={counterAccountId} onChange={(v) => setCounterAccountId(v ?? undefined)} placeholder={t('form.searchCounter')} ariaLabel={t('form.counterAccount')} />
          <AccountBalance accountId={counterAccountId} label={balanceLabel} />
        </FormField>
        <FormField id="v-amount" label={fieldLabel(Coins, t('form.amount'))} required>
          <NumberInput id="v-amount" value={amount} onValueChange={setAmount} currency={currency} placeholder="0.00" required />
        </FormField>
        <FormField id="v-currency" label={t('form.currency')} required>
          <CurrencySelect id="v-currency" value={currency} onChange={setCurrency} required />
        </FormField>
        <FormField id="v-ref" label={fieldLabel(Hash, t('form.reference'))}>
          <Input id="v-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder={t('form.referencePlaceholder')} maxLength={200} />
        </FormField>
        <FormField id="v-notes" label={fieldLabel(FileText, t('form.notes'))}>
          <Input id="v-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('form.notesPlaceholder')} maxLength={1000} />
        </FormField>
        {previewReady && (
          <div className="md:col-span-2 rounded-lg border border-border bg-bg-subtle p-3">
            <div className="mb-2 text-[0.7rem] font-bold uppercase tracking-wide text-fg-muted">{t('form.willPost')}</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
              <span className="inline-flex items-center gap-1.5">
                <span className="rounded bg-success/10 px-1.5 py-0.5 text-[0.7rem] font-bold text-success">{t('form.dr')}</span>
                <span className="font-medium text-fg">{drName}</span>
              </span>
              <ArrowRight className="h-4 w-4 flex-none text-fg-muted rtl:-scale-x-100" aria-hidden />
              <span className="inline-flex items-center gap-1.5">
                <span className="rounded bg-brand/10 px-1.5 py-0.5 text-[0.7rem] font-bold text-brand">{t('form.cr')}</span>
                <span className="font-medium text-fg">{crName}</span>
              </span>
              <span className="ms-auto font-mono text-base font-bold tabular-nums">{formatMoney(amount, { locale: i18n.language, currency })}</span>
            </div>
            <FxEquivalent amount={amount} currency={currency} date={date} className="mt-1 text-end" />
          </div>
        )}
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{isEdit ? t('form.saveChanges') : t('form.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
